import { FastField, FormikProps } from 'formik';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import { Box, Button } from '@mui/material';
import { EditProductModal } from '@modules/ContactManager/components/Tabs/components/Deals/components/Products/EditProducts/EditProductModal';
import { DealProduct } from '@modules/ContactManager/components/Tabs/components/Deals/components/Products/interfaces';
import { NContact } from '@modules/ContactManager/components/Tabs/components/Contact/validation';
import { formatCurrency, formatToNumber } from '@shared/commonFunctions';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { LenderSectionRow } from '@styles/ContactManager/DealCalculator/LenderSectionStyles';
import { DealCalculatorModel } from '../../../../models/DealCalculatorModel';
import { IEligibleLender } from '../../../../models/EligibleLenderModel';
import { requestUpdateProductRetail } from './queries';

export interface ILenderResponseFooter {
  gpVrp: number;
  totalGp: number;
}

interface ILenderSectionProductsProps {
  leadId: string | undefined;
  contactAddress?: NContact.Address;
  lenderCode: string | undefined;
  isExpanded(section: string): boolean;
  idx: number;
  maxProduct: number | undefined;
  formProps: FormikProps<DealCalculatorModel>;
  lender: IEligibleLender;
  setLenderResultFooterData?: React.Dispatch<React.SetStateAction<ILenderResponseFooter>>;
  getProducts: (products: DealProduct[]) => void;
  term?: number;
  isVoidEnable: boolean;
}
interface IProductBlock {
  isExpanded(section: string): boolean;
  productTypeName: string;
  maxAllowedRetail: number;
  cost: number;
  markup: number;
  planName: string;
}

const ProductBlock = ({
  isExpanded,
  productTypeName,
  maxAllowedRetail,
  cost,
  markup,
  planName,
}: IProductBlock): JSX.Element | null => {
  return isExpanded(productTypeName?.toLocaleLowerCase()) ? (
    <Box height={160}>
      <LenderSectionRow>
        <b>{formatCurrency(maxAllowedRetail)}</b>
      </LenderSectionRow>
      <LenderSectionRow>
        <b>{formatCurrency(cost)}</b>
      </LenderSectionRow>
      <LenderSectionRow>
        <b>{formatCurrency(markup)}</b>
      </LenderSectionRow>
      <LenderSectionRow>
        <b>{planName}</b>
      </LenderSectionRow>
    </Box>
  ) : null;
};

export const productTypeNames = {
  vsc: 'Vsc',
  gap: 'Gap',
  com: 'Com',
  paf: 'Paf',
  vrp: 'Vrp',
  mbi: 'Mbi',
};

const LenderSectionProducts = ({
  lenderCode,
  leadId,
  contactAddress,
  isExpanded,
  idx,
  maxProduct,
  formProps,
  lender,
  setLenderResultFooterData,
  getProducts,
  term = 0,
  isVoidEnable,
}: ILenderSectionProductsProps) => {
  const { data: products = [], mutate: refreshProducts } = useSWR<DealProduct[]>(
    `/${SERVICES.PRODUCT}/leads/dealcalculator?leadId=${leadId}&lenderCode=${lenderCode}`
  );

  const { data: globalProducts = [], mutate: refreshGlobalProducts } = useSWR<DealProduct[]>(
    `/${SERVICES.PRODUCT}/leads/dealcalculator?LeadId=${leadId}`
  );

  const lenderInputs = useMemo(() => `eligibleLenders[${idx}].inputs`, [idx]);

  const [editProductPosition, setEditProductPosition] = useState(0);
  const [productsList, setProductsList] = useState<DealProduct[]>([]);
  const [dealCalculatorLeadProductId, setLeadProductId] = useState(null);

  const [vscProduct, setVscProduct] = useState<DealProduct>();
  const [gapProduct, setGapProduct] = useState<DealProduct>();
  const [comProduct, setComProduct] = useState<DealProduct>();
  const [pafProduct, setPafProduct] = useState<DealProduct>();
  const [vrpProduct, setVrpProduct] = useState<DealProduct>();
  const [mbiProduct, setMbiProduct] = useState<DealProduct>();

  const { vscRetail, vrpRetail, comRetail, gapRetail, pafRetail, mbiRetail } = lender.inputs;

  const getProductFromList = (productTypeName: string, dealProductList: DealProduct[]) => {
    return dealProductList?.find((item: DealProduct) => item?.productTypeName === productTypeName);
  };

  useEffect(() => {
    if (products.length || globalProducts.length) {
      const dealProducts = products.length ? products : globalProducts;
      setProductsList(dealProducts);
      getProducts(dealProducts);
    }
  }, [products, globalProducts]);

  useEffect(() => {
    if (productsList.length === 0) {
      return;
    }

    setVscProduct(getProductFromList(productTypeNames.vsc, productsList));
    setMbiProduct(getProductFromList(productTypeNames.mbi, productsList));
    setGapProduct(getProductFromList(productTypeNames.gap, productsList));
    setComProduct(getProductFromList(productTypeNames.com, productsList));
    setPafProduct(getProductFromList(productTypeNames.paf, productsList));
    setVrpProduct(getProductFromList(productTypeNames.vrp, productsList));

    formProps.setFieldValue(`${lenderInputs}.vscRetail`, vscProduct?.retail || 0);
    formProps.setFieldValue(`${lenderInputs}.mbiRetail`, mbiProduct?.retail || 0);
    formProps.setFieldValue(`${lenderInputs}.gapRetail`, gapProduct?.retail || 0);
    formProps.setFieldValue(`${lenderInputs}.comRetail`, comProduct?.retail || 0);
    formProps.setFieldValue(`${lenderInputs}.pafRetail`, pafProduct?.retail || 0);
    formProps.setFieldValue(`${lenderInputs}.vrpRetail`, vrpProduct?.retail || 0);
  }, [productsList, vscProduct, gapProduct, comProduct, pafProduct, vrpProduct, mbiProduct, lenderInputs]);

  const updateRetail = async (productTypeName: string, newRetail: number) => {
    const updateRetailData = {
      productTypeName,
      newRetail: newRetail || 0,
      leadId: leadId || '',
      lenderCode: lenderCode || '',
      reason: '',
    };
    await requestUpdateProductRetail(updateRetailData);
    setProductsList([]);
    refreshProducts();
    refreshGlobalProducts();
  };

  const handleRetailValueChange = useCallback(
    async (value: string, productType: string) => {
      const retail = formatToNumber(value);

      const productDetail = getProductFromList(productType, productsList);
      if (!productDetail) {
        Notistack.generalError('No plan is selected for ' + productType + ' product');
      } else if (retail != productDetail.retail) {
        await updateRetail(productType, retail);
      }
    },
    [formProps.setFieldValue, productsList]
  );

  const getCost = (retail = 0, markup = 0) => retail - markup;

  useEffect(() => {
    setLenderResultFooterData &&
      setLenderResultFooterData({
        gpVrp: vrpProduct?.markup || 0,
        totalGp:
          (vscProduct?.markup || 0) +
          (vrpProduct?.markup || 0) +
          (gapProduct?.markup || 0) +
          (pafProduct?.markup || 0) -
          lender?.totalDealFeeAmount,
      });
  }, [vscProduct, vrpProduct, gapProduct, pafProduct]);

  const refreshData = () => {
    refreshProducts();
    refreshGlobalProducts();
  };

  const onPickPlanClick = useCallback(() => {
    if (term) {
      setEditProductPosition(prevState => prevState + 1);
    } else {
      Notistack.generalError('Please enter the term');
    }
  }, [term]);

  return (
    <>
      <Button
        style={{ height: '30px' }}
        variant="contained"
        onClick={onPickPlanClick}
      >
        Pick Plan
      </Button>
      <LenderSectionRow>{formatCurrency(maxProduct)}</LenderSectionRow>
      <LenderSectionRow>
        <FastField
          prefix="$"
          decimalScale={2}
          name={`${lenderInputs}.vscRetail`}
          component={NumberInputField}
          disabled={!vscProduct}
          onBlur={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
            handleRetailValueChange(value, productTypeNames.vsc);
          }}
        />
      </LenderSectionRow>
      <ProductBlock
        {...{
          isExpanded,
          productTypeName: productTypeNames.vsc,
          maxAllowedRetail: vscProduct?.maxAllowedRetail || 0,
          cost: getCost(vscRetail, vscProduct?.markup) || 0,
          markup: vscProduct?.markup || 0,
          planName: vscProduct?.plan.planName || '',
        }}
      />
      <LenderSectionRow>
        <FastField
          disabled
          prefix="$"
          decimalScale={2}
          name={`${lenderInputs}.mbiRetail`}
          component={NumberInputField}
          onBlur={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
            handleRetailValueChange(value, productTypeNames.mbi);
          }}
        />
      </LenderSectionRow>
      <ProductBlock
        {...{
          isExpanded,
          productTypeName: productTypeNames.mbi,
          maxAllowedRetail: mbiProduct?.maxAllowedRetail || 0,
          cost: getCost(mbiRetail, mbiProduct?.markup) || 0,
          markup: mbiProduct?.markup || 0,
          planName: mbiProduct?.plan.planName || '',
        }}
      />
      <LenderSectionRow>
        <FastField
          prefix="$"
          decimalScale={2}
          name={`${lenderInputs}.gapRetail`}
          component={NumberInputField}
          disabled={!gapProduct}
          onBlur={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
            handleRetailValueChange(value, productTypeNames.gap);
          }}
        />
      </LenderSectionRow>
      <ProductBlock
        {...{
          isExpanded,
          productTypeName: productTypeNames.gap,
          maxAllowedRetail: gapProduct?.maxAllowedRetail || 0,
          cost: getCost(gapRetail, gapProduct?.markup) || 0,
          markup: gapProduct?.markup || 0,
          planName: gapProduct?.plan.planName || '',
        }}
      />
      <LenderSectionRow>
        <FastField
          prefix="$"
          decimalScale={2}
          name={`${lenderInputs}.comRetail`}
          component={NumberInputField}
          disabled={!comProduct}
          onBlur={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
            handleRetailValueChange(value, productTypeNames.com);
          }}
        />
      </LenderSectionRow>
      <ProductBlock
        {...{
          isExpanded,
          productTypeName: productTypeNames.com,
          maxAllowedRetail: comProduct?.maxAllowedRetail || 0,
          cost: getCost(comRetail, comProduct?.markup) || 0,
          markup: comProduct?.markup || 0,
          planName: comProduct?.plan.planName || '',
        }}
      />
      <LenderSectionRow>
        <FastField
          prefix="$"
          decimalScale={2}
          name={`${lenderInputs}.pafRetail`}
          component={NumberInputField}
          disabled={!pafProduct}
          onBlur={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
            handleRetailValueChange(value, productTypeNames.paf);
          }}
        />
      </LenderSectionRow>
      <ProductBlock
        {...{
          isExpanded,
          productTypeName: productTypeNames.paf,
          maxAllowedRetail: pafProduct?.maxAllowedRetail || 0,
          cost: getCost(pafRetail, pafProduct?.markup) || 0,
          markup: pafProduct?.markup || 0,
          planName: pafProduct?.plan.planName || '',
        }}
      />
      <LenderSectionRow>
        <FastField
          prefix="$"
          decimalScale={2}
          name={`${lenderInputs}.vrpRetail`}
          component={NumberInputField}
          disabled={!vrpProduct}
          onBlur={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
            handleRetailValueChange(value, productTypeNames.vrp);
          }}
        />
      </LenderSectionRow>
      <ProductBlock
        {...{
          isExpanded,
          productTypeName: productTypeNames.vrp,
          maxAllowedRetail: vrpProduct?.maxAllowedRetail || 0,
          cost: getCost(vrpRetail, vrpProduct?.markup) || 0,
          markup: vrpProduct?.markup || 0,
          planName: vrpProduct?.plan.planName || '',
        }}
      />
      <EditProductModal
        isVoidEnable={isVoidEnable}
        editProductPosition={editProductPosition}
        setEditProductPosition={setEditProductPosition}
        reloadDeal={refreshData}
        contactAddress={contactAddress}
        preventDealSave={false}
        customData={{
          getRetailsFormUrl: `/${SERVICES.PRODUCT}/leads/dealcalculator?LeadId=${leadId}&LenderCode=${lenderCode}`,
          getTreeUrl: `/${SERVICES.PRODUCT}/leads/${leadId}/plans?lenderCode=${lenderCode}&termMonths=${term}&&state=${contactAddress?.state}`,
          submitUrl: `/${SERVICES.PRODUCT}/leads/${leadId}/dealcalculator`,
          type: 'dealCalculator',
          data: {
            leadId,
            lenderCode,
            setLeadProductId,
            dealCalculatorLeadProductId,
          },
        }}
      />
    </>
  );
};

export default LenderSectionProducts;
